import React, { useEffect } from "react";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string, number } from "yup";
import {
  addNewWarehouse,
  updateWarehouse,
  getAllWarehouses,
} from "src/app-redux/configuration/actions/configurationActions";
import { selectAllWarehousesList, loader } from "src/app-redux/configuration/configurationSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddWarehouse } from "src/common/typings/configuration";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { Prompt } from "react-router-dom";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
}));

export interface IForm extends IAddWarehouse {}

interface Props {}

export function InputFields({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [isEditMode, setisEditMode] = React.useState<boolean>(false);

  const loading = useAppSelector(loader);
  const allWarehousesRows = useAppSelector(selectAllWarehousesList);
  const allAreaUnits = ["sq. ft."];

  // Form Submission
  const validationSchema = object({
    name: string().required("This field is required").max(50, "Maximum 50 characters are allowed."),
    location: string()
      .required("This field is required")
      .max(50, "Maximum 50 characters are allowed."),
    areaCovered: number()
      .required("This field is required")
      .min(0, "Minimum 0 is allowed.")
      .max(9999999999, "Maximum 10 digits are allowed."),
    areaUnit: string().required("This field is required"),
  });
  const formik = useFormik<IAddWarehouse>({
    enableReinitialize: true,
    initialValues: {
      id: "",
      name: "",
      location: "",
      areaCovered: 0,
      areaUnit: "sq. ft.",
      isActive: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      let res;
      if (isEditMode) res = await dispatch(updateWarehouse(values));
      else res = await dispatch(addNewWarehouse(values));
      if (res?.payload) {
        dispatch(getAllWarehouses());
        actions.setSubmitting(false);
        actions.resetForm();
        setisEditMode(false);
        setIsDirty(false);
      }
    },
  });
  // End Form Submission
  const handleUpdateClick = (params: any) => {
    setisEditMode(true);
    formik.setFieldValue("id", params.id);
    formik.setFieldValue("name", params.name);
    formik.setFieldValue("location", params.location);
    formik.setFieldValue("areaCovered", params.areaCovered);
    formik.setFieldValue("areaUnit", params.areaUnit);
    formik.setFieldValue("isActive", params.isActive);
  };
  useEffect(() => {
    dispatch(getAllWarehouses());
  }, []);

  /* Transform Column */
  useEffect(() => {
    if (allWarehousesRows.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirty(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.field === "areaCovered"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return `${row.original.areaCovered} ${
                  row.original.areaUnit ? row.original.areaUnit : "sq. ft."
                } `;
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [allWarehousesRows]);

  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleChange(e: any) {
    formik.handleChange(e);
    e.target.value || JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
      ? setIsDirty(true)
      : setIsDirty(false);
  }

  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="flex-start"
                spacing={2}
              >
                <Grid container item xs={12} spacing={2}>
                  <Grid container item xs={12} spacing={2} style={{ paddingRight: "0px" }}>
                    <Grid
                      item
                      xs={12}
                      md={5}
                      lg={4}
                      style={{ paddingLeft: "0px", paddingRight: "0px" }}
                    >
                      <Box sx={{ marginRight: { xs: 0, lg: "5px" } }}>
                        <TextField
                          className="customTextField"
                          type="string"
                          required
                          size="small"
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Warehouse Name"
                          placeholder="Enter Warehouse Name"
                          name="name"
                          disabled={false}
                          value={formik.values.name}
                          onChange={handleChange}
                          error={formik.touched.name && Boolean(formik.errors.name)}
                          helperText={formik.touched.name && formik.errors.name}
                        />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={5}
                      lg={4}
                      style={{ paddingLeft: "0px", paddingRight: "0px" }}
                    >
                      <Box
                        sx={{
                          marginLeft: { xs: 0, md: "8px", lg: "4px" },
                          marginRight: { xs: 0, lg: "4px" },
                        }}
                      >
                        <TextField
                          className="customTextField"
                          type="string"
                          required
                          size="small"
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Address/Location"
                          placeholder="Enter Address/Location"
                          name="location"
                          disabled={false}
                          inputProps={{
                            maxLength: 80,
                          }}
                          value={formik.values.location}
                          onChange={handleChange}
                          error={formik.touched.location && Boolean(formik.errors.location)}
                          helperText={formik.touched.location && formik.errors.location}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={4} style={{ paddingLeft: "0px" }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="number"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        label="Area Covered"
                        placeholder="Enter Area Covered"
                        name="areaCovered"
                        disabled={false}
                        value={formik.values.areaCovered}
                        onChange={handleChange}
                        error={formik.touched.areaCovered && Boolean(formik.errors.areaCovered)}
                        helperText={formik.touched.areaCovered && formik.errors.areaCovered}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} style={{ paddingLeft: "0px" }}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        disabled={false}
                        label="Area Unit"
                        name="areaUnit"
                        placeholder="Enter Area Unit"
                        optionsType={allAreaUnits}
                        getOptionLabel={(option) => option}
                        required
                        value={
                          allAreaUnits.find((st: any) => st === formik.values.areaUnit) || null
                        }
                        onChange={(option: any) => {
                          option && option !== "" ? formik.setFieldValue("areaUnit", option) : "";
                        }}
                        error={formik.touched.areaUnit && Boolean(formik.errors.areaUnit)}
                        helperText={formik.touched.areaUnit && formik.errors.areaUnit}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2} style={{ paddingLeft: "0px" }}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2} style={{ paddingRight: 0 }}>
                    <Box mb={1} mt={1} className={"headerButtonsConfig"} style={{ float: "right" }}>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          formik.submitForm();
                        }}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>

                  <Grid md={12}>
                    <CommonMaterialGrid
                      defaultPerPageRecord={10}
                      height={400}
                      rows={allWarehousesRows || []}
                      columns={displayColumns}
                    />
                  </Grid>
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* End Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
